import React, { useState } from 'react';
import { CheckCircle2, Download, Trash2, ExternalLink, Facebook, Linkedin, Edit2, MessageSquare, ChevronDown, ChevronUp, AlertCircle, FileText, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function CreditApplication() {
  const [decisionOpen, setDecisionOpen] = useState(true);
  const [activeModal, setActiveModal] = useState<null | 'approve' | 'approveWithChanges' | 'reject'>(null);
  const [decisionMade, setDecisionMade] = useState<null | 'approved' | 'approvedWithChanges' | 'rejected'>(null);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const handleDecision = (decision: 'approved' | 'approvedWithChanges' | 'rejected') => {
    setDecisionMade(decision);
    setActiveModal(null);
    setShowAuditTrail(true);
    setNotesOpen(true);
  };
  return <div className="min-h-full bg-gray-50 p-6" data-id="element-153">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-154">
        {/* Header Card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4" data-id="element-155">
          <div className="flex items-center justify-between gap-4" data-id="element-156">
            {/* Left: Company Info + Notes */}
            <div className="shrink-0" data-id="element-157">
              <h1 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-158">
                DUNDER MIFFLIN PAPER COMPANY
              </h1>
              <div className="flex items-center gap-3 text-indigo-600 mb-2" data-id="element-159">
                <ExternalLink className="w-3.5 h-3.5 cursor-pointer" data-id="element-160" />
                <Facebook className="w-3.5 h-3.5 cursor-pointer" data-id="element-161" />
                <Linkedin className="w-3.5 h-3.5 cursor-pointer" data-id="element-162" />
                <Edit2 className="w-3.5 h-3.5 cursor-pointer" data-id="element-163" />
              </div>
              <div className="space-y-0.5 text-xs text-gray-600 mb-2" data-id="element-164">
                <div className="flex items-center gap-1.5" data-id="element-165">
                  <span className="font-semibold text-gray-900" data-id="element-166">
                    Credit Manager:
                  </span>
                  <span data-id="element-167">Dave Davidson</span>
                  <Edit2 className="w-3 h-3 text-indigo-600 cursor-pointer" data-id="element-168" />
                </div>
                <div className="flex items-center gap-1.5" data-id="element-169">
                  <span className="font-semibold text-gray-900" data-id="element-170">Added:</span>
                  <span data-id="element-171">3/27/2026</span>
                </div>
              </div>
              <button onClick={() => setNotesOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded flex items-center gap-1.5 text-xs font-medium relative" data-id="element-172">
                <MessageSquare className="w-3 h-3" data-id="element-173" />
                NOTES
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center" data-id="element-174">
                  3
                </span>
              </button>
            </div>

            {/* Right: Risk Gauge */}
            <div className="flex flex-col items-center shrink-0 mr-4" data-id="element-175">
              <div className="flex items-end gap-2 mb-1" data-id="element-176">
                <span className="text-xs text-gray-400 font-medium" data-id="element-177">0</span>
                <div className="relative w-64" data-id="element-178">
                  {/* Score label */}
                  <div className="absolute -top-6 left-[82%] -translate-x-1/2 bg-green-100 text-green-800 text-xs font-bold px-2 py-0.5 rounded" data-id="element-179">
                    82
                  </div>
                  {/* Bar */}
                  <div className="relative w-full h-6 flex rounded-full overflow-hidden" data-id="element-180">
                    <div className="w-1/3 bg-red-300" data-id="element-181"></div>
                    <div className="w-1/3 bg-yellow-300" data-id="element-182"></div>
                    <div className="w-1/3 bg-green-300" data-id="element-183"></div>
                  </div>
                  {/* Indicator line */}
                  <div className="absolute top-0 bottom-0 left-[82%] w-0.5 flex flex-col items-center" data-id="element-184">
                    <div className="w-[3px] h-full bg-gray-700 rounded-full" style={{
                    borderLeft: '2px dashed #374151'
                  }} data-id="element-185"></div>
                  </div>
                </div>
                <span className="text-xs text-gray-400 font-medium" data-id="element-186">100</span>
              </div>
              <div className="w-64 flex justify-between px-0 mt-1" data-id="element-187">
                <span className="text-xs text-red-500 font-medium pl-7" data-id="element-188">
                  Low
                </span>
                <span className="text-xs text-yellow-600 font-medium" data-id="element-189">
                  Medium
                </span>
                <span className="text-xs text-gray-600 font-medium pr-7" data-id="element-190">
                  High
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-t border-gray-200 mt-4 pt-3" data-id="element-191">
            {['OVERVIEW', 'NOTIFICATIONS', 'DETAILS', 'FINANCIALS', 'PEOPLE', 'APPLICATIONS'].map(tab => <button key={tab} className={`flex-1 text-center py-1.5 text-xs font-semibold ${tab === 'APPLICATIONS' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-192">
                {tab}
              </button>)}
          </div>
        </div>

        {/* Credit Decision Section */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg shadow-sm overflow-hidden" data-id="element-193">
          {/* Header Row */}
          <button onClick={() => setDecisionOpen(!decisionOpen)} className="w-full p-4 flex items-center justify-between bg-indigo-100 hover:bg-indigo-150 transition-colors" data-id="element-194">
            <div className="flex items-center gap-4" data-id="element-195">
              <span className="font-bold text-gray-900" data-id="element-196">Credit Decision</span>
              <span className="border border-green-500 text-green-600 px-2.5 py-0.5 rounded-full text-sm font-medium" data-id="element-197">
                Ready
              </span>
              <span className="text-sm text-gray-500" data-id="element-198">
                Date: March 27, 2026
              </span>
              <span className="text-sm text-gray-300" data-id="element-199">·</span>
              <span className="text-sm font-medium text-gray-700" data-id="element-200">
                Recommended Credit Limit: $50,000
              </span>
            </div>
            {decisionOpen ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-201" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-202" />}
          </button>

          {/* Expanded Content */}
          {decisionOpen && <div className="p-6 border-t border-indigo-200" data-id="element-203">
              <div className="grid grid-cols-2 gap-x-12 gap-y-6 mb-8" data-id="element-204">
                {/* Left Column */}
                <div className="space-y-4" data-id="element-205">
                  <div data-id="element-206">
                    <p className="text-sm text-gray-500 mb-1" data-id="element-207">
                      Recommended Decision
                    </p>
                    <p className="text-xl font-bold text-gray-900" data-id="element-208">
                      Approve for $50,000
                    </p>
                  </div>
                  <div data-id="element-209">
                    <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-210">
                      Terms
                    </p>
                    <div className="text-sm text-gray-600 space-y-0.5" data-id="element-211">
                      <p data-id="element-212">Payment Terms Days: 30</p>
                      <p data-id="element-213">Discount: 5%</p>
                      <p data-id="element-214">Discount Days: 10</p>
                      <p data-id="element-215">Late Pay Penalty: 1.5%</p>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4" data-id="element-216">
                  <div data-id="element-217">
                    <div className="flex items-center gap-2 mb-1" data-id="element-218">
                      <p className="text-sm text-gray-500" data-id="element-219">Policy Applied</p>
                      <span className="text-sm text-gray-300" data-id="element-220">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-221">
                        (View Policy)
                      </button>
                    </div>
                    <p className="font-bold text-gray-900" data-id="element-222">
                      My Company's Credit Policy v1
                    </p>
                  </div>

                  <div data-id="element-223">
                    <div className="flex items-center gap-2 mb-1" data-id="element-224">
                      <p className="text-sm text-gray-500" data-id="element-225">
                        Credit Application
                      </p>
                      <span className="text-sm text-gray-300" data-id="element-226">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-227">
                        (View Application)
                      </button>
                    </div>
                    <p className="text-sm text-gray-700" data-id="element-228">
                      Submitted on: March 27, 2026
                    </p>
                  </div>

                  <div data-id="element-229">
                    <div className="flex items-center gap-2 mb-1" data-id="element-230">
                      <p className="text-sm text-gray-500" data-id="element-231">
                        Recommendation Summary
                      </p>
                      <span className="text-sm text-gray-300" data-id="element-232">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-233">
                        (Full Reasoning)
                      </button>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed" data-id="element-234">
                      Dunder Mifflin Paper Company, Inc. is approved for credit
                      based on strong business fundamentals, an established
                      77-year operating history, and positive trade references.
                      However, the credit limit is conservatively set due to the
                      applicant's limited initial order size and the need to
                      establish a direct payment history, warranting a measured
                      initial credit exposure.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-4 gap-4" data-id="element-235">
                <button onClick={() => setActiveModal('approve')} className="bg-green-700 hover:bg-green-800 text-white py-3 rounded-lg font-semibold text-sm" data-id="element-236">
                  APPROVE
                </button>
                <button onClick={() => setActiveModal('approveWithChanges')} className="bg-yellow-600 hover:bg-yellow-700 text-white py-3 rounded-lg font-semibold text-sm" data-id="element-237">
                  APPROVE WITH CHANGES
                </button>
                <button onClick={() => setActiveModal('reject')} className="bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold text-sm" data-id="element-238">
                  REJECT
                </button>
                <button className="border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 py-3 rounded-lg font-semibold text-sm" data-id="element-239">
                  REGENERATE RECOMMENDATION
                </button>
              </div>
            </div>}
        </div>

        {/* Decision Modals */}
        <AnimatePresence data-id="element-240">
          {activeModal && <>
              {/* Backdrop */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setActiveModal(null)} className="fixed inset-0 bg-black/50 z-50" data-id="element-241" />

              {/* Modal */}
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-242">
                <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6" data-id="element-243">
                  {activeModal === 'approve' && <>
                      <h2 className="text-xl font-bold text-gray-900 mb-5" data-id="element-244">
                        Approve Recommendation
                      </h2>
                      <div className="space-y-4" data-id="element-245">
                        <div className="grid grid-cols-3 gap-3" data-id="element-246">
                          <fieldset className="border border-gray-300 rounded px-3 pt-1.5 pb-2 col-span-2" data-id="element-247">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-248">
                              Credit Limit
                            </legend>
                            <input type="text" defaultValue="50,000" className="w-full text-sm text-gray-900 outline-none" data-id="element-249" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-1.5 pb-2" data-id="element-250">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-251">
                              Currency
                            </legend>
                            <select className="w-full text-sm text-gray-900 outline-none bg-transparent" data-id="element-252">
                              <option data-id="element-253">USD</option>
                            </select>
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-1.5 pb-2" data-id="element-254">
                          <legend className="text-xs text-gray-500 px-1" data-id="element-255">
                            Payment Terms Days
                          </legend>
                          <input type="text" defaultValue="30" className="w-full text-sm text-gray-900 outline-none" data-id="element-256" />
                        </fieldset>
                        <div className="grid grid-cols-2 gap-3" data-id="element-257">
                          <fieldset className="border border-gray-300 rounded px-3 pt-1.5 pb-2" data-id="element-258">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-259">
                              Discount (%)
                            </legend>
                            <input type="text" placeholder="" className="w-full text-sm text-gray-900 outline-none" data-id="element-260" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-1.5 pb-2" data-id="element-261">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-262">
                              Discount Days
                            </legend>
                            <input type="text" placeholder="" className="w-full text-sm text-gray-900 outline-none" data-id="element-263" />
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-1.5 pb-2" data-id="element-264">
                          <legend className="text-xs text-gray-500 px-1" data-id="element-265">
                            Late Pay Penalty (%)
                          </legend>
                          <input type="text" placeholder="" className="w-full text-sm text-gray-900 outline-none" data-id="element-266" />
                        </fieldset>
                        <fieldset className="border border-gray-300 rounded px-3 pt-1.5 pb-2" data-id="element-267">
                          <legend className="text-xs text-gray-500 px-1" data-id="element-268">
                            Reasoning
                          </legend>
                          <textarea rows={3} className="w-full text-sm text-gray-900 outline-none resize-none" data-id="element-269" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-3 mt-5" data-id="element-270">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-4 py-2 hover:bg-indigo-50 rounded text-sm" data-id="element-271">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('approved')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 py-2 rounded text-sm" data-id="element-272">
                          APPROVE
                        </button>
                      </div>
                    </>}

                  {activeModal === 'approveWithChanges' && <>
                      <h2 className="text-xl font-bold text-gray-900 mb-5" data-id="element-273">
                        Approve and Input Values
                      </h2>
                      <div className="space-y-4" data-id="element-274">
                        <div className="grid grid-cols-3 gap-3" data-id="element-275">
                          <fieldset className="border border-gray-300 rounded px-3 pt-1.5 pb-2 col-span-2" data-id="element-276">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-277">
                              Credit Limit *
                            </legend>
                            <input type="text" defaultValue="50,000" className="w-full text-sm text-gray-900 outline-none" data-id="element-278" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-1.5 pb-2" data-id="element-279">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-280">
                              Currency
                            </legend>
                            <select className="w-full text-sm text-gray-900 outline-none bg-transparent" data-id="element-281">
                              <option data-id="element-282">USD</option>
                            </select>
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-1.5 pb-2" data-id="element-283">
                          <legend className="text-xs text-gray-500 px-1" data-id="element-284">
                            Payment Terms Days
                          </legend>
                          <input type="text" defaultValue="30" className="w-full text-sm text-gray-900 outline-none" data-id="element-285" />
                        </fieldset>
                        <div className="grid grid-cols-2 gap-3" data-id="element-286">
                          <fieldset className="border border-gray-300 rounded px-3 pt-1.5 pb-2" data-id="element-287">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-288">
                              Discount (%)
                            </legend>
                            <input type="text" placeholder="" className="w-full text-sm text-gray-900 outline-none" data-id="element-289" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-1.5 pb-2" data-id="element-290">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-291">
                              Discount Days
                            </legend>
                            <input type="text" placeholder="" className="w-full text-sm text-gray-900 outline-none" data-id="element-292" />
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-1.5 pb-2" data-id="element-293">
                          <legend className="text-xs text-gray-500 px-1" data-id="element-294">
                            Late Pay Penalty (%)
                          </legend>
                          <input type="text" placeholder="" className="w-full text-sm text-gray-900 outline-none" data-id="element-295" />
                        </fieldset>
                        <fieldset className="border border-gray-300 rounded px-3 pt-1.5 pb-2" data-id="element-296">
                          <legend className="text-xs text-gray-500 px-1" data-id="element-297">
                            Reasoning
                          </legend>
                          <textarea rows={3} className="w-full text-sm text-gray-900 outline-none resize-none" data-id="element-298" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-3 mt-5" data-id="element-299">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-4 py-2 hover:bg-indigo-50 rounded text-sm" data-id="element-300">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('approvedWithChanges')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 py-2 rounded text-sm" data-id="element-301">
                          CONFIRM APPROVAL
                        </button>
                      </div>
                    </>}

                  {activeModal === 'reject' && <>
                      <h2 className="text-xl font-bold text-gray-900 mb-5" data-id="element-302">
                        Reject
                      </h2>
                      <div className="space-y-4" data-id="element-303">
                        <fieldset className="border border-gray-300 rounded px-3 pt-1.5 pb-2" data-id="element-304">
                          <legend className="text-xs text-gray-500 px-1" data-id="element-305">
                            Reasoning
                          </legend>
                          <textarea rows={4} className="w-full text-sm text-gray-900 outline-none resize-none" data-id="element-306" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-3 mt-5" data-id="element-307">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-4 py-2 hover:bg-indigo-50 rounded text-sm" data-id="element-308">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('rejected')} className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2 rounded text-sm" data-id="element-309">
                          CONFIRM REJECTION
                        </button>
                      </div>
                    </>}
                </div>
              </motion.div>
            </>}
        </AnimatePresence>

        {/* Notes Slide-out Panel */}
        <AnimatePresence data-id="element-310">
          {notesOpen && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setNotesOpen(false)} className="fixed inset-0 bg-black/20 z-40" data-id="element-311" />
              <motion.div initial={{
            x: '100%'
          }} animate={{
            x: 0
          }} exit={{
            x: '100%'
          }} transition={{
            type: 'spring',
            damping: 30,
            stiffness: 300
          }} className="fixed top-0 right-0 h-full w-[420px] bg-white shadow-2xl z-50 flex flex-col" data-id="element-312">
                {/* Panel Header */}
                <div className="p-5 border-b border-gray-200 flex items-start justify-between" data-id="element-313">
                  <div className="flex items-center gap-3" data-id="element-314">
                    <MessageSquare className="w-5 h-5 text-gray-700" data-id="element-315" />
                    <div data-id="element-316">
                      <h2 className="text-lg font-bold text-gray-900" data-id="element-317">Notes</h2>
                      <p className="text-sm text-gray-500" data-id="element-318">
                        DUNDER MIFFLIN PAPER COMPANY
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setNotesOpen(false)} className="text-gray-400 hover:text-gray-600 p-1" data-id="element-319">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-320">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" data-id="element-321" />
                    </svg>
                  </button>
                </div>

                {/* Add Note Button */}
                <div className="px-5 pt-4 pb-3" data-id="element-322">
                  <button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2" data-id="element-323">
                    <span className="text-lg leading-none" data-id="element-324">+</span>
                    ADD NOTE
                  </button>
                </div>

                {/* Filter */}
                <div className="px-5 pb-4" data-id="element-325">
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-700 bg-white" data-id="element-326">
                    <option data-id="element-327">All Notes</option>
                    <option data-id="element-328">System Notes</option>
                    <option data-id="element-329">User Notes</option>
                  </select>
                </div>

                {/* Notes / Audit Trail Content */}
                <div className="flex-1 overflow-y-auto px-5 pb-6" data-id="element-330">
                  <div className="space-y-0" data-id="element-331">
                    {/* Application Submitted */}
                    <div className="flex gap-3" data-id="element-332">
                      <div className="flex flex-col items-center" data-id="element-333">
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-500 mt-2 shrink-0" data-id="element-334" />
                        <div className="w-0.5 flex-1 bg-gray-200 mt-1" data-id="element-335" />
                      </div>
                      <div className="pb-5" data-id="element-336">
                        <span className="inline-block text-[10px] font-medium text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded mb-1" data-id="element-337">
                          SYSTEM
                        </span>
                        <p className="text-xs text-gray-400 mb-0.5" data-id="element-338">
                          March 27, 2026, 10:15 AM
                        </p>
                        <p className="text-sm font-semibold text-gray-900" data-id="element-339">
                          Application Submitted
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-340">
                          Credit application submitted by Pam Beesly on behalf
                          of Dunder Mifflin Paper Company.
                        </p>
                      </div>
                    </div>

                    {/* Assigned */}
                    <div className="flex gap-3" data-id="element-341">
                      <div className="flex flex-col items-center" data-id="element-342">
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-500 mt-2 shrink-0" data-id="element-343" />
                        <div className={`w-0.5 flex-1 mt-1 ${decisionMade ? 'bg-gray-200' : 'bg-transparent'}`} data-id="element-344" />
                      </div>
                      <div className="pb-5" data-id="element-345">
                        <span className="inline-block text-[10px] font-medium text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded mb-1" data-id="element-346">
                          SYSTEM
                        </span>
                        <p className="text-xs text-gray-400 mb-0.5" data-id="element-347">
                          March 27, 2026, 10:16 AM
                        </p>
                        <p className="text-sm font-semibold text-gray-900" data-id="element-348">
                          Assigned to Credit Manager
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-349">
                          Application assigned to Dave Davidson for review.
                        </p>
                      </div>
                    </div>

                    {/* Decision - only shows after a decision is made */}
                    {decisionMade && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="flex gap-3" data-id="element-350">
                        <div className="flex flex-col items-center" data-id="element-351">
                          <div className={`w-2.5 h-2.5 rounded-full mt-2 shrink-0 ${decisionMade === 'approved' ? 'bg-green-500' : decisionMade === 'approvedWithChanges' ? 'bg-yellow-500' : 'bg-red-500'}`} data-id="element-352" />
                        </div>
                        <div className="pb-5" data-id="element-353">
                          <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded mb-1 ${decisionMade === 'approved' ? 'text-green-700 bg-green-50 border border-green-200' : decisionMade === 'approvedWithChanges' ? 'text-yellow-700 bg-yellow-50 border border-yellow-200' : 'text-red-700 bg-red-50 border border-red-200'}`} data-id="element-354">
                            {decisionMade === 'approved' ? 'APPROVED' : decisionMade === 'approvedWithChanges' ? 'MODIFIED' : 'REJECTED'}
                          </span>
                          <p className="text-xs text-gray-400 mb-0.5" data-id="element-355">
                            March 29, 2026, 2:34 PM
                          </p>
                          <p className="text-sm font-semibold text-gray-900" data-id="element-356">
                            {decisionMade === 'approved' && 'Credit Approved for $50,000'}
                            {decisionMade === 'approvedWithChanges' && 'Credit Approved with Modifications'}
                            {decisionMade === 'rejected' && 'Credit Application Rejected'}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-357">
                            Decision made by Dave Davidson.
                          </p>
                        </div>
                      </motion.div>}
                  </div>

                  {!decisionMade && <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-358">
                      <p className="text-xs text-gray-400 text-center" data-id="element-359">
                        Awaiting credit decision...
                      </p>
                    </div>}
                </div>
              </motion.div>
            </>}
        </AnimatePresence>

        {/* Application Content */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm" data-id="element-360">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex justify-between items-start" data-id="element-361">
            <div data-id="element-362">
              <div className="flex items-center gap-3 mb-2" data-id="element-363">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-364">
                  Credit Application
                </h2>
                <span className="border border-indigo-200 text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5" data-id="element-365">
                  <Search className="w-3.5 h-3.5" data-id="element-366" />
                  Ready for Review
                </span>
              </div>
              <div className="text-sm text-gray-600 space-y-1" data-id="element-367">
                <p data-id="element-368">
                  <span className="font-semibold text-gray-900" data-id="element-369">
                    Started On:
                  </span>{' '}
                  March 27, 2026
                </p>
                <p data-id="element-370">
                  <span className="font-semibold text-gray-900" data-id="element-371">
                    Submitted On:
                  </span>{' '}
                  March 27, 2026
                </p>
              </div>
            </div>
            <button className="border border-indigo-200 text-indigo-600 hover:bg-indigo-50 px-4 py-2 rounded text-sm font-medium flex items-center gap-2" data-id="element-372">
              <Download className="w-4 h-4" data-id="element-373" />
              DOWNLOAD PDF
            </button>
          </div>

          {/* Applicant Details */}
          <div className="p-6 border-b border-gray-200" data-id="element-374">
            <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-375">
              Applicant Details
            </h3>
            <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-376">
              <div data-id="element-377">
                <p className="text-sm text-gray-500 mb-1" data-id="element-378">Name</p>
                <p className="text-gray-900" data-id="element-379">Pam Beesly</p>
              </div>
              <div data-id="element-380">
                <p className="text-sm text-gray-500 mb-1" data-id="element-381">Company</p>
                <p className="text-gray-900" data-id="element-382">
                  Dunder Mifflin Paper Company Inc
                </p>
              </div>
              <div data-id="element-383">
                <p className="text-sm text-gray-500 mb-1" data-id="element-384">Role</p>
                <p className="text-gray-900" data-id="element-385">Office Administrator</p>
              </div>
              <div data-id="element-386">
                <p className="text-sm text-gray-500 mb-1" data-id="element-387">Department</p>
                <p className="text-gray-900" data-id="element-388">Administration</p>
              </div>
              <div data-id="element-389">
                <p className="text-sm text-gray-500 mb-1" data-id="element-390">Email</p>
                <div className="flex items-center gap-2 mb-3" data-id="element-391">
                  <p className="text-gray-900" data-id="element-392">pam.beesly@dundermifflin.com</p>
                  <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-393" />
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3" data-id="element-394">
                  <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" data-id="element-395" />
                  <div data-id="element-396">
                    <p className="font-semibold text-gray-900 text-sm" data-id="element-397">
                      Email Verification
                    </p>
                    <p className="text-sm text-gray-700 mt-1" data-id="element-398">
                      Email verified and matches company domain.
                    </p>
                  </div>
                </div>
              </div>
              <div data-id="element-399">
                <p className="text-sm text-gray-500 mb-1" data-id="element-400">Phone Number</p>
                <p className="text-gray-900" data-id="element-401">(570) 555-0126</p>
              </div>
              <div data-id="element-402">
                <p className="text-sm text-gray-500 mb-1" data-id="element-403">
                  Location (IP Address)
                </p>
                <div className="flex items-center gap-2 mb-3" data-id="element-404">
                  <p className="text-gray-900" data-id="element-405">Scranton, PA (136.61.36.131)</p>
                  <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-406" />
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3" data-id="element-407">
                  <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" data-id="element-408" />
                  <div data-id="element-409">
                    <p className="font-semibold text-gray-900 text-sm" data-id="element-410">
                      IP Address Verification
                    </p>
                    <p className="text-sm text-gray-700 mt-1" data-id="element-411">
                      Applicant location matches company headquarters.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Business Contact Information */}
          <div className="p-6 border-b border-gray-200" data-id="element-412">
            <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-413">
              Business Contact Information
            </h3>
            <div className="grid grid-cols-2 gap-12" data-id="element-414">
              <div data-id="element-415">
                <p className="text-sm text-gray-500 mb-1" data-id="element-416">Legal Company Name</p>
                <p className="text-gray-900" data-id="element-417">
                  Dunder Mifflin Paper Company Inc
                </p>
              </div>
              <div data-id="element-418">
                <p className="text-sm text-gray-500 mb-1" data-id="element-419">
                  Please attach W-9 Form below:
                </p>
                <div className="border border-indigo-200 rounded px-3 py-2 flex items-center gap-2 text-indigo-600 text-sm max-w-xs bg-white" data-id="element-420">
                  <FileText className="w-4 h-4" data-id="element-421" />
                  W9.svg
                </div>
              </div>
            </div>
          </div>

          {/* Physical Address */}
          <div className="p-6 border-b border-gray-200" data-id="element-422">
            <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-423">
              Physical Address
            </h3>
            <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-424">
              <div data-id="element-425">
                <p className="text-sm text-gray-500 mb-1" data-id="element-426">Street Address</p>
                <p className="text-gray-900" data-id="element-427">1725 Slough Avenue</p>
              </div>
              <div data-id="element-428">
                <p className="text-sm text-gray-500 mb-1" data-id="element-429">City</p>
                <p className="text-gray-900" data-id="element-430">Scranton</p>
              </div>
              <div data-id="element-431">
                <p className="text-sm text-gray-500 mb-1" data-id="element-432">State</p>
                <p className="text-gray-900" data-id="element-433">PA</p>
              </div>
              <div data-id="element-434">
                <p className="text-sm text-gray-500 mb-1" data-id="element-435">Zip Code</p>
                <p className="text-gray-900" data-id="element-436">18505</p>
              </div>
              <div data-id="element-437">
                <p className="text-sm text-gray-500 mb-1" data-id="element-438">Country</p>
                <p className="text-gray-900" data-id="element-439">United States</p>
              </div>
            </div>
          </div>

          {/* Principals */}
          <div className="p-6 border-b border-gray-200" data-id="element-440">
            <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-441">Principals</h3>
            <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-442">
              <div data-id="element-443">
                <p className="text-sm text-gray-500 mb-1" data-id="element-444">Full Name</p>
                <p className="text-gray-900" data-id="element-445">Michael Scott</p>
              </div>
              <div data-id="element-446">
                <p className="text-sm text-gray-500 mb-1" data-id="element-447">Address</p>
                <p className="text-gray-900" data-id="element-448">1725 Slough Avenue</p>
              </div>
              <div data-id="element-449">
                <p className="text-sm text-gray-500 mb-1" data-id="element-450">Position</p>
                <p className="text-gray-900" data-id="element-451">Regional Manager</p>
              </div>
              <div data-id="element-452">
                <p className="text-sm text-gray-500 mb-1" data-id="element-453">Phone #</p>
                <p className="text-gray-900" data-id="element-454">(570) 555-0126</p>
              </div>
            </div>
          </div>

          {/* Business Details */}
          <div className="p-6 border-b border-gray-200" data-id="element-455">
            <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-456">
              Business Details
            </h3>
            <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-457">
              <div data-id="element-458">
                <p className="text-sm text-gray-500 mb-1" data-id="element-459">
                  Date Business Started
                </p>
                <p className="text-gray-900" data-id="element-460">1949-01-01</p>
              </div>
              <div data-id="element-461">
                <p className="text-sm text-gray-500 mb-1" data-id="element-462">Company Website</p>
                <p className="text-gray-900" data-id="element-463">https://www.dundermifflin.com</p>
              </div>
              <div data-id="element-464">
                <p className="text-sm text-gray-500 mb-1" data-id="element-465">Business Type</p>
                <p className="text-gray-900" data-id="element-466">Corporation</p>
              </div>
              <div data-id="element-467">
                <p className="text-sm text-gray-500 mb-1" data-id="element-468">
                  If "Other", please specify:
                </p>
                <p className="text-gray-900" data-id="element-469"></p>
              </div>
              <div data-id="element-470">
                <p className="text-sm text-gray-500 mb-1" data-id="element-471">
                  Accounting Contact Name
                </p>
                <p className="text-gray-900" data-id="element-472">Angela Martin</p>
              </div>
              <div data-id="element-473">
                <p className="text-sm text-gray-500 mb-1" data-id="element-474">
                  Accounting Contact Email
                </p>
                <p className="text-gray-900" data-id="element-475">angela.martin@dundermifflin.com</p>
              </div>
            </div>
          </div>

          {/* Trade References */}
          <div className="p-6 border-b border-gray-200" data-id="element-476">
            <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-477">
              Trade References
            </h3>
            <div className="space-y-4 mb-6" data-id="element-478">
              {['Weyerhaeuser Paper', 'Hammermill', 'Sabre'].map(ref => <div key={ref} className="border border-gray-200 rounded-lg p-4 flex justify-between items-center" data-id="element-479">
                  <div data-id="element-480">
                    <h4 className="font-bold text-gray-900 mb-1" data-id="element-481">{ref}</h4>
                    <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-482">
                      <span className="flex items-center gap-1.5" data-id="element-483">
                        <FileText className="w-4 h-4" data-id="element-484" />
                        Completed on March 27, 2026
                      </span>
                      <span className="flex items-center gap-1.5" data-id="element-485">
                        <MessageSquare className="w-4 h-4" data-id="element-486" />1 email sent
                      </span>
                    </div>
                  </div>
                  <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-487" />
                </div>)}
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-id="element-488">
              <h4 className="font-bold text-gray-900 mb-1" data-id="element-489">
                Additional Trade References
              </h4>
              <p className="text-sm text-gray-500 mb-4" data-id="element-490">
                No additional references requested
              </p>
              <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2" data-id="element-491">
                <ExternalLink className="w-4 h-4" data-id="element-492" />
                REQUEST ADDITIONAL TRADE REFERENCES
              </button>
            </div>
          </div>

          {/* Banking Information */}
          <div className="p-6 border-b border-gray-200" data-id="element-493">
            <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-494">
              Banking Information
            </h3>
            <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-495">
              <div data-id="element-496">
                <p className="text-sm text-gray-500 mb-1" data-id="element-497">Bank Name</p>
                <p className="text-gray-900" data-id="element-498">Scranton National Bank</p>
              </div>
              <div data-id="element-499">
                <p className="text-sm text-gray-500 mb-1" data-id="element-500">Contact</p>
                <p className="text-gray-900" data-id="element-501">Robert Lipton</p>
              </div>
              <div data-id="element-502">
                <p className="text-sm text-gray-500 mb-1" data-id="element-503">Type of Account</p>
                <p className="text-gray-900" data-id="element-504">Checking</p>
              </div>
              <div data-id="element-505">
                <p className="text-sm text-gray-500 mb-1" data-id="element-506">Account #</p>
                <p className="text-gray-900" data-id="element-507">481726350</p>
              </div>
              <div data-id="element-508">
                <p className="text-sm text-gray-500 mb-1" data-id="element-509">Email</p>
                <p className="text-gray-900" data-id="element-510">r.lipton@scrantonnational.com</p>
              </div>
              <div data-id="element-511">
                <p className="text-sm text-gray-500 mb-1" data-id="element-512">City</p>
                <p className="text-gray-900" data-id="element-513">Scranton</p>
              </div>
              <div data-id="element-514">
                <p className="text-sm text-gray-500 mb-1" data-id="element-515">State</p>
                <p className="text-gray-900" data-id="element-516">PA</p>
              </div>
              <div data-id="element-517">
                <p className="text-sm text-gray-500 mb-1" data-id="element-518">Phone #</p>
                <p className="text-gray-900" data-id="element-519">570-555-0198</p>
              </div>
              <div data-id="element-520">
                <p className="text-sm text-gray-500 mb-1" data-id="element-521">Fax #</p>
                <p className="text-gray-900" data-id="element-522">570-555-0199</p>
              </div>
            </div>
          </div>

          {/* Signature */}
          <div className="p-6" data-id="element-523">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-524">Signature</h3>
            <div className="border-t border-indigo-200 pt-4 mb-6" data-id="element-525">
              <h4 className="font-bold text-gray-900 mb-3" data-id="element-526">
                Terms & Conditions
              </h4>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4" data-id="element-527">
                <p className="text-sm text-gray-700 leading-relaxed" data-id="element-528">
                  I understand that should my account become delinquent (terms
                  of sale are based upon date of invoice) and that if the
                  creditor elects to employ a third party to collect monies due,
                  my company will be responsible for not only the original
                  amount billed, but also all costs associated with the
                  collection efforts including but not limited to reasonable
                  attorney fees. Cash In Advance restrictions may be placed on
                  any past due account. Customer will notify the creditor, in
                  writing of any ownership changes or legal means of doing
                  business by the company under which credit was established.
                </p>
              </div>
              <p className="text-sm font-semibold text-gray-900 mb-6" data-id="element-529">
                By signing the below you give Dunder Mifflin Paper Company Inc.,
                permission to check your bank and trade references.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-530">
              <div data-id="element-531">
                <p className="text-sm text-gray-500 mb-1" data-id="element-532">Signature</p>
                <div className="border border-gray-200 rounded-lg p-4 h-24 flex items-end" data-id="element-533">
                  <svg viewBox="0 0 300 80" className="w-56 h-16" data-id="element-534">
                    <path d="M10,60 C20,55 25,30 35,25 C45,20 50,50 60,45 C70,40 75,20 85,25 C95,30 100,55 110,50 C115,47 118,35 125,30 C132,25 140,40 150,35 C155,32 160,28 170,30 C180,32 185,45 195,40 C200,38 205,25 215,30 C220,33 225,45 230,42" fill="none" stroke="#1e3a8a" strokeWidth="2" strokeLinecap="round" data-id="element-535" />
                  </svg>
                </div>
              </div>
              <div data-id="element-536">
                <p className="text-sm text-gray-500 mb-1" data-id="element-537">Printed Name</p>
                <p className="text-gray-900" data-id="element-538">Dwight K. Schrute</p>
              </div>
              <div data-id="element-539">
                <p className="text-sm text-gray-500 mb-1" data-id="element-540">Title</p>
                <p className="text-gray-900" data-id="element-541">Assistant Regional Manager</p>
              </div>
              <div data-id="element-542">
                <p className="text-sm text-gray-500 mb-1" data-id="element-543">Date</p>
                <p className="text-gray-900" data-id="element-544">2026-03-27</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}